// CookieConsent.jsx
import React, { useEffect, useState } from 'react'
import { Button, Modal, Switch, Icon, Row, Col, Card, Divider } from 'antd'
import './CookieConsent.scss'
import { useTranslation } from 'react-i18next'

const COOKIE_KEY = 'cookie-consent'
const COOKIE_VERSION = '1.0' // For tracking consent version changes
const USER_LANGUAGE_KEY = 'user-language-preference' // Track if user manually changed language

export default function CookieConsent() {
  const [visible, setVisible] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [analytics, setAnalytics] = useState(false)
  const [advertising, setAdvertising] = useState(false)
  const [tracking, setTracking] = useState(false)
  const [functional, setFunctional] = useState(false)
  const [isGermanRegion, setIsGermanRegion] = useState(false)

  const { t, i18n } = useTranslation()

  const detectGermanRegion = async () => {
    const results = {
      browserLang: false,
      timezone: false,
      geolocation: false,
      finalResult: false
    }

    try {
      // Method 1: Enhanced browser language detection
      const languages = navigator.languages || [navigator.language]
      const hasGermanLang = languages.some(lang =>
        lang.toLowerCase().startsWith('de') ||
        lang.toLowerCase().includes('de-')
      )
      results.browserLang = hasGermanLang

      // Method 2: Enhanced timezone detection
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      const germanTimezones = [
        'Europe/Berlin',
        'Europe/Vienna',
        'Europe/Zurich',
        'Europe/Amsterdam',
        'Europe/Luxembourg'
      ]
      results.timezone = germanTimezones.includes(timezone)

      // Method 3: Multiple IP geolocation services (more reliable)
      const geoServices = [
        'https://ipapi.co/json/',
        'https://api.ipify.org?format=json',
        'https://httpbin.org/ip'
      ]

      for (const service of geoServices) {
        try {
          const controller = new AbortController()
          const timeoutId = setTimeout(() => controller.abort(), 3000)

          const response = await fetch(service, {
            signal: controller.signal,
            headers: {
              'Accept': 'application/json'
            }
          })
          clearTimeout(timeoutId)

          if (!response.ok) continue

          const data = await response.json()
          let countryCode = data.country_code || data.country || null

          if (countryCode) {
            const germanCountries = ['DE', 'AT', 'CH', 'LI']
            results.geolocation = germanCountries.includes(countryCode.toUpperCase())
            break
          }
        } catch (geoError) {
          console.warn(`Geolocation service ${service} failed:`, geoError.message)
          continue
        }
      }

      const conflictingSignals = (results.browserLang || results.timezone) && !results.geolocation

      if (conflictingSignals) {
        console.warn('Conflicting location signals detected - possible VPN usage')
        results.finalResult = results.geolocation
      } else {
        results.finalResult = results.browserLang || results.timezone || results.geolocation
      }

      console.log('German region detection results:', results)
      return results.finalResult

    } catch (error) {
      console.error('German region detection failed:', error)
      return false
    }
  }

  const detectGermanRegionAdvanced = async () => {
    const detectionMethods = []

    try {
      const languages = navigator.languages || [navigator.language]
      const germanLangs = languages.filter(lang =>
        lang.toLowerCase().match(/^de(-|$)/)
      )
      detectionMethods.push({
        method: 'browserLanguage',
        detected: germanLangs.length > 0,
        confidence: germanLangs.length > 0 ? 0.7 : 0,
        details: germanLangs
      })

      try {
        const date = new Date()
        const germanFormat = date.toLocaleDateString('de-DE')
        const currentFormat = date.toLocaleDateString()
        const isGermanFormat = germanFormat === currentFormat

        detectionMethods.push({
          method: 'dateFormat',
          detected: isGermanFormat,
          confidence: isGermanFormat ? 0.6 : 0,
          details: { current: currentFormat, german: germanFormat }
        })
      } catch (e) {
        console.warn('Date format detection failed:', e)
      }

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      const germanTimezones = [
        'Europe/Berlin', 'Europe/Vienna', 'Europe/Zurich',
        'Europe/Luxembourg', 'Europe/Amsterdam'
      ]
      const isGermanTimezone = germanTimezones.includes(timezone)

      detectionMethods.push({
        method: 'timezone',
        detected: isGermanTimezone,
        confidence: isGermanTimezone ? 0.8 : 0,
        details: timezone
      })

      const geoResult = await Promise.race([
        fetch('https://ipapi.co/json/').then(r => r.json()),
        fetch('https://api.ipgeolocation.io/ipgeo?apiKey=free').then(r => r.json()),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 5000))
      ]).catch(() => null)

      if (geoResult) {
        const countryCode = (geoResult.country_code || geoResult.country_code2 || '').toUpperCase()
        const isGermanCountry = ['DE', 'AT', 'CH', 'LI'].includes(countryCode)

        detectionMethods.push({
          method: 'geolocation',
          detected: isGermanCountry,
          confidence: isGermanCountry ? 0.9 : 0,
          details: { country: countryCode, service: 'ipapi' }
        })
      }

      const totalConfidence = detectionMethods.reduce((sum, method) => {
        return sum + (method.detected ? method.confidence : 0)
      }, 0)

      const maxPossibleConfidence = detectionMethods.reduce((sum, method) => {
        return sum + method.confidence
      }, 0)

      const confidenceRatio = maxPossibleConfidence > 0 ? totalConfidence / maxPossibleConfidence : 0
      const isGermanRegion = confidenceRatio > 0.3

      console.log('Advanced German region detection:', {
        methods: detectionMethods,
        totalConfidence,
        maxPossibleConfidence,
        confidenceRatio,
        result: isGermanRegion
      })

      return isGermanRegion

    } catch (error) {
      console.error('Advanced German region detection failed:', error)
      return false
    }
  }

  const detectGermanRegionWithFallback = async () => {
    const results = await Promise.allSettled([
      detectGermanRegion(),
      detectGermanRegionAdvanced()
    ])

    const basicResult = results[0].status === 'fulfilled' ? results[0].value : false
    const advancedResult = results[1].status === 'fulfilled' ? results[1].value : false

    if (basicResult !== advancedResult) {
      console.warn('Detection methods disagree:', { basic: basicResult, advanced: advancedResult })
    }

    return basicResult || advancedResult
  }

  // Check if user has manually set a language preference
  const hasUserLanguagePreference = () => {
    return localStorage.getItem(USER_LANGUAGE_KEY) !== null
  }

  // Function to switch to German language (only if user hasn't set preference)
  const switchToGerman = () => {
    if (!hasUserLanguagePreference() && i18n.language !== 'de') {
      i18n.changeLanguage('german')
    }
  }

  const switchToEnglish = () => {
    if (!hasUserLanguagePreference() && i18n.language !== 'en') {
      i18n.changeLanguage('english')
    }
  }

  useEffect(() => {
    const initializeComponent = async () => {
      // Check if i18n language is already set and different from browser default
      // This indicates user or app has already chosen a language
      const currentLang = i18n.language
      const browserLang = navigator.language.split('-')[0]

      // If current language is set and it's not just the browser default, 
      // mark it as user preference to prevent auto-switching
      if (currentLang && currentLang !== 'en' && currentLang !== browserLang) {
        localStorage.setItem(USER_LANGUAGE_KEY, 'true')
      }

      // First, check existing consent in localStorage
      const saved = localStorage.getItem(COOKIE_KEY)
      if (saved) {
        try {
          const consent = JSON.parse(saved)

          if (consent.version !== COOKIE_VERSION || !consent.timestamp) {
            setVisible(true)
          } else {
            const { analytics, advertising, tracking, functional, timestamp } = consent

            const consentAge = Date.now() - timestamp
            const twelveMonths = 365 * 24 * 60 * 60 * 1000

            if (consentAge > twelveMonths) {
              setVisible(true)
            } else {
              setAnalytics(analytics || false)
              setAdvertising(advertising || false)
              setTracking(tracking || false)
              setFunctional(functional || false)

              if (analytics) loadGoogleAnalytics()
              if (advertising) loadAdvertisingScripts()
              if (tracking) loadTrackingScripts()
              if (functional) loadFunctionalScripts()

              // Early return: do not run region detection or change language if consent already present
              return
            }
          }
        } catch (error) {
          console.error('Error parsing cookie consent:', error)
          setVisible(true)
        }
      }

      // Only perform region detection and auto-switch language if:
      // 1. No valid consent found AND
      // 2. User hasn't manually set a language preference
      if (!hasUserLanguagePreference()) {
        const isGerman = await detectGermanRegionWithFallback()
        console.log('Detected German region:', isGerman)
        setIsGermanRegion(isGerman)

        // Switch to German or English based on detection
        if (isGerman) {
          switchToGerman()
        } else {
          switchToEnglish()
        }
      } else {
        console.log('User has set language preference, skipping auto-detection')
        // Still detect region for cookie consent purposes, but don't change language
        const isGerman = await detectGermanRegionWithFallback()
        setIsGermanRegion(isGerman)
      }

      // Show banner if no valid consent
      if (!saved) {
        setVisible(true)
      }
    }

    initializeComponent()
  }, [])

  // Listen for language changes and mark as user preference
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      console.log('Language changed to:', lng)
      localStorage.setItem(USER_LANGUAGE_KEY, 'true')
    }

    i18n.on('languageChanged', handleLanguageChange)

    return () => {
      i18n.off('languageChanged', handleLanguageChange)
    }
  }, [i18n])

  const handleSave = preferences => {
    const consentData = {
      ...preferences,
      timestamp: Date.now(),
      version: COOKIE_VERSION,
      userAgent: navigator.userAgent,
      region: isGermanRegion ? 'DE' : 'OTHER',
      language: i18n.language
    }

    localStorage.setItem(COOKIE_KEY, JSON.stringify(consentData))

    if (preferences.analytics) loadGoogleAnalytics()
    if (preferences.advertising) loadAdvertisingScripts()
    if (preferences.tracking) loadTrackingScripts()
    if (preferences.functional) loadFunctionalScripts()

    setVisible(false)
    setShowModal(false)
  }

  const loadGoogleAnalytics = () => {
    if (window.ga) return

    const s = document.createElement('script')
    s.src = 'https://www.google-analytics.com/analytics.js'
    s.async = true
    document.head.appendChild(s)

    window.ga =
      window.ga ||
      function () {
        ; (window.ga.q = window.ga.q || []).push(arguments)
      }
    window.ga('create', 'UA-134596782-1', 'auto')
    window.ga('send', 'pageview')
  }

  const loadAdvertisingScripts = () => {
    console.log('Loading advertising scripts...')
  }

  const loadTrackingScripts = () => {
    console.log('Loading tracking scripts...')
  }

  const loadFunctionalScripts = () => {
    if (window.$zoho) return

    const s = document.createElement('script')
    s.src = 'https://salesiq.zohopublic.com/widget'
    s.defer = true
    s.id = 'zsiqscript'
    document.body.appendChild(s)
    window.$zoho = window.$zoho || {}
    window.$zoho.salesiq = window.$zoho.salesiq || {
      widgetcode:
        'siq8dfb245d8cbac14929a7f8a8cc6be4f3eebf7b8c862560ded4e97dd2ba0d9f88',
      values: {},
      ready: function () { }
    }
  }

  const handleAcceptAll = () => {
    handleSave({
      analytics: true,
      advertising: true,
      tracking: true,
      functional: true
    })
  }

  const handleRejectAll = () => {
    handleSave({
      analytics: false,
      advertising: false,
      tracking: false,
      functional: false
    })
  }

  const handleSavePreferences = () => {
    handleSave({ analytics, advertising, tracking, functional })
  }

  const handleWithdrawConsent = () => {
    localStorage.removeItem(COOKIE_KEY)
    setVisible(true)
    window.location.reload()
  }

  if (!visible) return null

  return (
    <>
      <div className="cookie-banner">
        <div className="cookie-banner-content">
          <div className="cookie-banner-icon">
            <Icon
              type="info-circle"
              style={{ fontSize: '24px', color: '#fff' }}
            />
          </div>
          <div className="cookie-banner-text">
            <h4>{t('v4:manage_cookies')}</h4>
            <p>{t('v4:cookie_banner_text')}</p>
          </div>
        </div>

        <div className="cookie-actions">
          <Button
            type="primary"
            icon="check"
            onClick={handleAcceptAll}
            className="accept-btn"
            size="large"
          >
            {t('v4:accept_all_cookies')}
          </Button>
          <div className="secondary-actions">
            <Button
              icon="close"
              onClick={handleRejectAll}
              className="reject-btn"
            >
              {t('v4:reject_all')}
            </Button>
            <Button
              icon="setting"
              onClick={() => setShowModal(true)}
              className="manage-btn"
            >
              {t('v4:customize')}
            </Button>
          </div>
        </div>
      </div>

      <Modal
        title={
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '8px'
            }}
          >
            {t('v4:cookie_preferences_title')}
            <div className="privacy-info">
              <span>{t('v4:privacy_subtitle')}</span>
            </div>
          </div>
        }
        visible={showModal}
        onCancel={() => setShowModal(false)}
        footer={[
          <Button key="reject" icon="close" onClick={handleRejectAll}>
            {t('v4:reject_all')}
          </Button>,
          <Button key="cancel" onClick={() => setShowModal(false)}>
            {t('buttons:cancel')}
          </Button>,
          <Button
            key="save"
            type="primary"
            icon="save"
            onClick={handleSavePreferences}
          >
            {t('v4:save_choices')}
          </Button>
        ]}
        width={700}
        className="cookie-modal"
      >
        <div className="cookie-preferences">
          <Card className="cookie-card">
            <div className="cookie-category">
              <div className="cookie-category-header">
                <Icon
                  type="safety-certificate"
                  style={{ color: '#52c41a', marginRight: '8px' }}
                />
                <span className="cookie-category-title">
                  {t('v4:strictly_necessary')}
                </span>
                <Switch
                  checked={true}
                  disabled={true}
                  style={{ marginLeft: 'auto' }}
                />
              </div>
              <p className="cookie-category-description">
                {t('v4:strictly_necessary_desc')}
              </p>
            </div>

            <div className="cookie-category">
              <div className="cookie-category-header">
                <Icon
                  type="bar-chart"
                  style={{ color: '#5B6EE2', marginRight: '8px' }}
                />
                <span className="cookie-category-title">
                  {t('v4:analytics_performance')}
                </span>
                <Switch
                  checked={analytics}
                  onChange={setAnalytics}
                  style={{ marginLeft: 'auto' }}
                />
              </div>
              <p className="cookie-category-description">
                {t('v4:analytics_performance_desc')}
              </p>
            </div>

            <div className="cookie-category">
              <div className="cookie-category-header">
                <Icon
                  type="customer-service"
                  style={{ color: '#13c2c2', marginRight: '8px' }}
                />
                <span className="cookie-category-title">
                  {t('v4:functional')}
                </span>
                <Switch
                  checked={functional}
                  onChange={setFunctional}
                  style={{ marginLeft: 'auto' }}
                />
              </div>
              <p className="cookie-category-description">
                {t('v4:functional_desc')}
              </p>
            </div>
          </Card>

          <div className="gdpr-footer">
            <p>
              <Icon type="info-circle" style={{ marginRight: '4px' }} />
              <strong>{t('v4:your_rights')}</strong>{' '}
              {t('v4:rights_description')}
            </p>
            <Button
              type="link"
              icon="delete"
              onClick={handleWithdrawConsent}
              className="withdraw-consent-btn"
            >
              {t('v4:withdraw_consent')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}